import * as React from 'react';
import { Popover } from '@base-ui-components/react/popover';
import { cn } from '@/shared/lib/cn';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { X } from 'lucide-react';
import { DateIcon } from '@/shared/config/entity-icons';
import { Dialog, DialogContent } from '@/shared/ui/Dialog/Dialog';
import { IconButton } from '@/shared/ui/IconButton/IconButton';
import styles from './picker.module.css';

const SHEET_QUERY = '(max-width: 768px)';

export interface PickerShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  popup: React.ReactNode;
  triggerLabel: string;
  onClear?: () => void;
  clearLabel?: string;
  disabled?: boolean;
  finalFocus?: React.RefObject<HTMLElement | null>;
  filled?: boolean;
  className?: string;
}

export const PickerShell: React.FC<PickerShellProps> = ({
  open,
  onOpenChange,
  children,
  popup,
  triggerLabel,
  onClear,
  clearLabel = 'Очистить',
  disabled,
  finalFocus,
  filled,
  className,
}) => {
  const boxRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const sheet = useMediaQuery(SHEET_QUERY);

  const trigger = (props: React.ButtonHTMLAttributes<HTMLButtonElement> = {}) => (
    <IconButton
      ref={triggerRef}
      size="sm"
      label={triggerLabel}
      className={styles.trigger}
      disabled={disabled}
      {...props}
    >
      <DateIcon size={16} aria-hidden />
    </IconButton>
  );

  const box = (button: React.ReactNode) => (
    <div
      ref={boxRef}
      className={cn(styles.box, className)}
      data-open={open || undefined}
      data-filled={filled ? '' : undefined}
      data-clearable={onClear && !disabled ? '' : undefined}
    >
      {children}
      {onClear && !disabled && (
        <IconButton
          size="sm"
          label={clearLabel}
          className={styles.clear}
          onClick={onClear}
        >
          <X size={14} aria-hidden />
        </IconButton>
      )}
      {button}
    </div>
  );

  if (sheet) {
    return (
      <>
        {box(
          trigger({
            'aria-haspopup': 'dialog',
            'aria-expanded': open,
            onClick: () => onOpenChange(true),
          }),
        )}
        <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
          <DialogContent
            flush
            className={styles.sheet}
            aria-label={triggerLabel}
            finalFocus={triggerRef}
          >
            <div className={styles.sheetBody}>{popup}</div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      {box(<Popover.Trigger disabled={disabled} render={trigger()} />)}
      <Popover.Portal>
        <Popover.Positioner
          anchor={boxRef}
          side="bottom"
          align="start"
          sideOffset={6}
          className={styles.positioner}
        >
          <Popover.Popup className={styles.popup} finalFocus={finalFocus}>
            {popup}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};
